/**
 * Block 13 PR-G Phase 4A — Local auto-counter для именования договоров
 * «Договор №{N} от {date}» (PRD §10.13.1 «Мои договоры»).
 *
 * Mirror Block 12 `quoteCounter.ts` pattern. Counter — глобальный (один на
 * пользователя, не per-furnitureType — в отличие от КП). После backend
 * (Phase 9+) — заменится network-call для cross-device consistency, BACKLOG.
 *
 * **Atomic read-modify-write:** `getNextContractCounter()` использует
 * readwrite-транзакцию — два параллельных save'а не приведут к race.
 *
 * **Graceful degradation:** если IDB unavailable → fallback на in-memory counter.
 */

import { openDB, type IDBPDatabase, type DBSchema } from 'idb';

export const CONTRACT_COUNTERS_DB_NAME = 'contract-counters';
export const CONTRACT_COUNTERS_DB_VERSION = 1;
export const CONTRACT_COUNTERS_STORE = 'counters';

/** Single global counter — key 'global'. */
const COUNTER_KEY = 'global';

export interface ContractCountersDBSchema extends DBSchema {
  counters: {
    key: string;
    value: number;
  };
}

let dbPromise: Promise<IDBPDatabase<ContractCountersDBSchema>> | null = null;
let memoryFallback = 0;

function isIdbAvailable(): boolean {
  return typeof indexedDB !== 'undefined';
}

function logWarn(scope: string, err: unknown): void {
  if (import.meta.env.DEV) {
    const errName = err instanceof Error ? err.name : 'unknown';
    console.warn(`[contractCounter] ${scope} failed`, errName);
  }
}

async function getDb(): Promise<IDBPDatabase<ContractCountersDBSchema> | null> {
  if (!isIdbAvailable()) return null;
  if (!dbPromise) {
    dbPromise = openDB<ContractCountersDBSchema>(
      CONTRACT_COUNTERS_DB_NAME,
      CONTRACT_COUNTERS_DB_VERSION,
      {
        upgrade(db) {
          if (!db.objectStoreNames.contains(CONTRACT_COUNTERS_STORE)) {
            db.createObjectStore(CONTRACT_COUNTERS_STORE);
          }
        },
        terminated() {
          dbPromise = null;
        },
        blocking() {
          dbPromise?.then((db) => db.close()).catch(() => {});
          dbPromise = null;
        },
      },
    );
  }
  try {
    return await dbPromise;
  } catch (err) {
    logWarn('openDB', err);
    dbPromise = null;
    return null;
  }
}

/**
 * Atomic increment globalного counter договоров. Возвращает следующее
 * available число (1, 2, 3, ...).
 */
export async function getNextContractCounter(): Promise<number> {
  const db = await getDb();
  if (!db) {
    memoryFallback += 1;
    return memoryFallback;
  }

  try {
    const tx = db.transaction(CONTRACT_COUNTERS_STORE, 'readwrite');
    const current = (await tx.store.get(COUNTER_KEY)) ?? 0;
    const next = current + 1;
    await tx.store.put(next, COUNTER_KEY);
    await tx.done;
    return next;
  } catch (err) {
    logWarn('getNextContractCounter', err);
    memoryFallback += 1;
    return memoryFallback;
  }
}

/** Peek current value без increment'а. */
export async function peekContractCounter(): Promise<number> {
  const db = await getDb();
  if (!db) return memoryFallback;
  try {
    return (await db.get(CONTRACT_COUNTERS_STORE, COUNTER_KEY)) ?? 0;
  } catch (err) {
    logWarn('peekContractCounter', err);
    return memoryFallback;
  }
}

/** Test cleanup utility. */
export function _resetContractCounterCacheForTests(): void {
  dbPromise = null;
  memoryFallback = 0;
}
